import React, { useCallback } from 'react';
import { Route } from 'react-router-dom';
import styled from 'styled-components';
import { Divider, FormHelperText } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ConfigFieldControl, ConfigTextField } from '@sierralabs/core-ui';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import DocumentListPage from '../../documents/DocumentListPage';
import { DocumentListPageContext, useDocumentListPageContext } from '../../documents/DocumentListPageContext';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useFindNearestRouteComponent } from '../../hooks/site-route/useFindNearestRouteComponent';
import { useSiteRouteComponents } from '../../hooks/site-route/useSiteRouteComponents';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledConfigFieldControl = styled(ConfigFieldControl) `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
export const SiteDocumentListPage = props => {
    const component = useSubscribeComponentChanges(SiteDocumentListPagePlugin, props);
    const { isHover, hoverProps } = useSiteContext();
    const classes = ['site-document-list-page'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteDocumentListPagePlugin }, props),
        React.createElement(DocumentListPageProvider, { component: component },
            React.createElement(DocumentListPageRoot, { className: classes.join(' '), containerProps: hoverProps(component) }))));
};
const DocumentListPageRoot = props => {
    const { basePath } = useDocumentListPageContext();
    return (React.createElement(Route, { exact: true, path: [basePath, `${basePath}/folder/:folderId`] },
        React.createElement(DocumentListPage, Object.assign({}, props))));
};
export const SiteDocumentListPageConfigurator = props => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { component, update } = useConfigComponent(props.component);
    const route = useFindNearestRouteComponent(component);
    const routePath = (_a = route === null || route === void 0 ? void 0 : route.props) === null || _a === void 0 ? void 0 : _a.path;
    return (React.createElement(React.Fragment, null,
        ((_b = route === null || route === void 0 ? void 0 : route.props) === null || _b === void 0 ? void 0 : _b.exact) && (React.createElement(Alert, { severity: 'warning' },
            "Site Route ",
            React.createElement("strong", null, "exact"),
            " property should be unchecked to allow access to ",
            React.createElement("strong", null, "folder/:folderId"),
            " path.")),
        React.createElement(StyledConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, value: ((_c = component.props) === null || _c === void 0 ? void 0 : _c.basePath) || '', label: 'Base Route Path', placeholder: 'Redirect path for Document links', onChange: event => update('props.basePath', event.target.value) }),
            React.createElement(FormHelperText, null,
                "Uses the route path by default.",
                routePath && (React.createElement(React.Fragment, null,
                    React.createElement("br", null),
                    "(route: ",
                    React.createElement("strong", null, routePath),
                    ")")))),
        React.createElement(Divider, null),
        React.createElement(SiteRouteAndParamPicker, { component: component, routeId: (_d = component.props) === null || _d === void 0 ? void 0 : _d.fileRouteId, routeParam: (_e = component.props) === null || _e === void 0 ? void 0 : _e.fileRouteParam, siteRoutePickerProps: {
                label: 'File Route',
                placeholder: 'Redirect path for File detail page'
            }, onChange: value => {
                updateRouteAndParam(value, 'props.fileRouteId', 'props.fileRouteParam').forEach(([path, value]) => update(path, value));
            } }),
        React.createElement(Divider, null),
        React.createElement(SiteRouteAndParamPicker, { component: component, routeId: (_f = component.props) === null || _f === void 0 ? void 0 : _f.userRouteId, routeParam: (_g = component.props) === null || _g === void 0 ? void 0 : _g.userRouteParam, siteRoutePickerProps: {
                label: 'User Route',
                placeholder: 'Redirect path for User detail page'
            }, onChange: value => {
                updateRouteAndParam(value, 'props.userRouteId', 'props.userRouteParam').forEach(([path, value]) => update(path, value));
            } })));
};
export const DocumentListPageProvider = props => {
    var _a, _b, _c, _d;
    const { component, children } = props;
    const routes = useSiteRouteComponents();
    const route = useFindNearestRouteComponent(component);
    // use the route path as basePath by default
    const basePath = ((_a = component.props) === null || _a === void 0 ? void 0 : _a.basePath) || ((_b = route === null || route === void 0 ? void 0 : route.props) === null || _b === void 0 ? void 0 : _b.path) || '/';
    const { fileRouteParam, userRouteParam } = component.props || {};
    const fileRoute = routes.find(route => { var _a; return route.id === ((_a = component.props) === null || _a === void 0 ? void 0 : _a.fileRouteId); });
    const filePath = ((_c = fileRoute === null || fileRoute === void 0 ? void 0 : fileRoute.props) === null || _c === void 0 ? void 0 : _c.path) || '';
    const userRoute = routes.find(route => { var _a; return route.id === ((_a = component.props) === null || _a === void 0 ? void 0 : _a.userRouteId); });
    const userPath = ((_d = userRoute === null || userRoute === void 0 ? void 0 : userRoute.props) === null || _d === void 0 ? void 0 : _d.path) || '';
    const toFilePath = useCallback((fileId) => {
        return filePath.replace(`:${fileRouteParam}`, (fileId || 0).toString());
    }, [filePath, fileRouteParam]);
    const toUserPath = useCallback((userId) => {
        return userPath.replace(`:${userRouteParam}`, userId.toString());
    }, [userPath, userRouteParam]);
    return (React.createElement(DocumentListPageContext.Provider, { value: { basePath, toFilePath, toUserPath } }, children));
};
export const SiteDocumentListPagePlugin = {
    name: 'Site Document List Page',
    type: 'site-document-list-page',
    component: SiteDocumentListPage,
    configurator: SiteDocumentListPageConfigurator
};
//# sourceMappingURL=SiteDocumentListPagePlugin.js.map